import { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Chip, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Ingredient } from '../../../models/ingredient';
import { getIngredients } from '../../../api/ingredient-api';

interface IngredientSelectorProps {
  onSearch: (ingredientIds: string[]) => void;
  loading?: boolean;
  availableIngredients?: Ingredient[];
}

const IngredientSelector = ({
  onSearch,
  loading,
  availableIngredients: externalIngredients,
}: IngredientSelectorProps) => {
  const [localIngredients, setLocalIngredients] = useState<Ingredient[]>([]);
  const [selectedIngredients, setSelectedIngredients] = useState<Ingredient[]>([]);
  const [error, setError] = useState<string | null>(null);

  const ingredients = externalIngredients ?? localIngredients;

  useEffect(() => {
    if (externalIngredients) return;
    getIngredients()
      .then(setLocalIngredients)
      .catch(() => {
        setError('Nepodařilo se načíst ingredience');
      });
  }, [externalIngredients]);

  const handleSearch = () => {
    const ids = selectedIngredients.map((i) => i.id);
    if (ids.length > 0) {
      onSearch(ids);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <Autocomplete
        multiple
        options={ingredients}
        getOptionLabel={(option) => option.name}
        value={selectedIngredients}
        onChange={(_, newValue) => setSelectedIngredients(newValue)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => <Chip label={option.name} size="small" {...getTagProps({ index })} />)
        }
        renderInput={(params) => <TextField {...params} label="Vyber ingredience, které máš doma" size="small" />}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        size="small"
        sx={{ mb: 1 }}
      />
      <Button
        variant="contained"
        startIcon={<SearchIcon />}
        onClick={handleSearch}
        disabled={selectedIngredients.length === 0 || loading}
        size="small"
      >
        {loading ? 'Hledám...' : 'Hledat recepty'}
      </Button>
    </Box>
  );
};

export default IngredientSelector;
